import { computed, Directive, effect, ElementRef, inject, input, Renderer2 } from '@angular/core';
import { presenceBoolean } from '@ion-ui/transforms';

import { ButtonDirective } from '../button/button.directive';

@Directive({
  selector: 'button[ionButton][loading]',
  standalone: true,
  host: {
    '[attr.aria-busy]': "loading() ? 'true' : null",
    // augment the disabled state of the button to gain control over the disabled state when loading is true
    '[disabled]': 'effectiveDisabled()',
    '[class.ion-button--loading]': 'loading()',
  },
})
export class ButtonLoadingDirective {
  private hostElement = inject<ElementRef<HTMLButtonElement>>(ElementRef);
  private renderer = inject(Renderer2);
  // use the existing ButtonDirective to access its disabled state
  private buttonDirective = inject(ButtonDirective, { self: true });
  private spinnerElement: HTMLElement | null = null;

  readonly loading = input(false, { transform: (value: unknown) => presenceBoolean(value, 'ionButton loading') });
  readonly effectiveDisabled = computed(() => this.loading() || this.buttonDirective.disabled());

  constructor() {
    effect(() => {
      if (this.loading()) {
        this.ensureSpinner();
        return;
      }

      this.removeSpinner();
    });
  }

  private ensureSpinner(): void {
    if (this.spinnerElement) {
      return;
    }

    const host = this.hostElement.nativeElement;
    const spinner = host.ownerDocument.createElement('span');

    this.renderer.addClass(spinner, 'ion-button__spinner');
    this.renderer.setAttribute(spinner, 'aria-hidden', 'true');
    this.renderer.insertBefore(host, spinner, host.firstChild);

    this.spinnerElement = spinner;
  }

  private removeSpinner(): void {
    if (!this.spinnerElement) {
      return;
    }

    this.renderer.removeChild(this.hostElement.nativeElement, this.spinnerElement);
    this.spinnerElement = null;
  }
}
